// src/pages/ProductDetails/ProductDetailsPage.jsx
import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import CartIcon from "../../assets/icons/CartIcon";
import { productService } from "../../services/product.service";
import "./ProductDetailPage.css";
import ProductCard from "../../components/common/ProductCard";
import ReviewSection from "./ReviewSection";
import RazorpayEmiWidget from "./RazorpayEmiWidget";
import BatteryShowcase from "./BatteryShowCase";
import InverterShowcase from "./InverterShowCase";
import InverterDisplayModes from "./InverterDisplayModes";
import ChargerShowcase from "./ChargerShowCase";
import BMSShowcase from "./BmsShowCase";
import ZynvertEdge from "./ZynvertEdge";
import { checkPincodeService } from "../../services/shipment.service";
import { auth } from "../../config/firebase";
import InverterComparison from "./InverterComparison";
import BackupChart from "./BackupChart";
import InverterInterface from "./InverterInterface";
import SolarReady from "./SolarReady";
import BatteryLifeComparison from "./BatteryLifeComparison";

const FALLBACK_IMAGE =
  "https://placehold.co/800x800/eeeeee/999999?text=No+Image";

/* ─────────────────────────────────────────────────────────────
   PRODUCT TYPE DETECTION
   ───────────────────────────────────────────────────────────── */
const detectProductType = (parsedSpecs, productName = "") => {
  const cat = (parsedSpecs?.category || "").toLowerCase().replace(/\s+/g, "_");
  const name = productName.toLowerCase();

  if (cat === "integrated_inverter") return "integrated_inverter";
  if (cat === "inverter") return "inverter";
  if (cat === "bms") return "bms";
  if (cat === "battery") return "battery";
  if (cat.includes("charger")) return "charger";

  if (name.includes("integrated") && name.includes("inverter"))
    return "integrated_inverter";
  if (name.includes("inverter")) return "inverter";
  if (name.includes("bms")) return "bms";
  if (name.includes("charger")) return "charger";
  if (
    name.includes("battery") ||
    name.includes("lifepo4") ||
    name.includes("lithium") ||
    name.includes("li-ion")
  )
    return "battery";

  return "unknown";
};

/* ─────────────────────────────────────────────────────────────
   TECHNICAL SNAPSHOT CONFIG
   ───────────────────────────────────────────────────────────── */
const getSnapshotItems = (productType, parsedSpecs, name) => {
  const warranty = parsedSpecs?.warranty?.duration_years
    ? `${parsedSpecs.warranty.duration_years} Year`
    : null;

  switch (productType) {
    case "battery": {
      const bat = parsedSpecs?.battery_specifications || {};
      let voltage = null;
      let capacity = null;
      let chemistry = bat.chemistry || null;

      const voltMatch = name?.match(/(\d+(\.\d+)?)\s*(v|volt)/i);
      if (voltMatch) voltage = `${voltMatch[1]}V`;
      else if (bat.nominal_voltage_v) voltage = `${bat.nominal_voltage_v}V`;

      const capMatch = name?.match(/(\d+(\.\d+)?)\s*(mah|ah)/i);
      if (capMatch) {
        capacity =
          capMatch[3].toLowerCase() === "ah"
            ? `${capMatch[1]}Ah`
            : `${capMatch[1]}mAh`;
      } else if (bat.capacity_ah) {
        capacity = `${bat.capacity_ah}Ah`;
      } else if (bat.energy_wh) {
        capacity = `${bat.energy_wh}Wh`;
      }

      const lowerName = (name || "").toLowerCase();
      if (
        lowerName.includes("lifepo4") ||
        lowerName.includes("lithium iron") ||
        lowerName.includes("lithium phosphate")
      )
        chemistry = "LiFePO4";
      else if (lowerName.includes("li-ion") || lowerName.includes("liion"))
        chemistry = "Li-ion";

      return [
        { label: "Voltage", value: voltage || "—" },
        { label: "Capacity", value: capacity || "—" },
        { label: "Chemistry", value: chemistry || "Li-ion" },
        { label: "Warranty", value: warranty || "1 Year" },
      ];
    }
    case "inverter": {
      const e = parsedSpecs?.electrical_specifications || {};
      return [
        {
          label: "Rated Power",
          value: e.rated_power_w ? `${e.rated_power_w}W` : "—",
        },
        {
          label: "Capacity",
          value: e.capacity_va ? `${e.capacity_va}VA` : "—",
        },
        {
          label: "Efficiency",
          value: e.peak_efficiency_percent
            ? `${e.peak_efficiency_percent}%`
            : "—",
        },
        { label: "Warranty", value: warranty || "2 Year" },
      ];
    }
    case "integrated_inverter": {
      const inv = parsedSpecs?.inverter_specifications || {};
      const ibat = parsedSpecs?.integrated_battery_specifications || {};
      return [
        {
          label: "Rated Power",
          value: inv.rated_power_w ? `${inv.rated_power_w}W` : "—",
        },
        {
          label: "Battery",
          value: ibat.capacity_wh ? `${ibat.capacity_wh}Wh` : "—",
        },
        { label: "Chemistry", value: ibat.battery_type || "LiFePO4" },
        { label: "Warranty", value: warranty || "3 Year" },
      ];
    }
    case "bms": {
      const bms = parsedSpecs?.bms_specifications || {};
      const maxA = bms.max_continuous_discharge_a
        ? `${bms.max_continuous_discharge_a}A`
        : null;
      const peakA = bms.peak_discharge_current_a
        ? String(bms.peak_discharge_current_a)
            .replace(/\(.*\)/, "")
            .trim()
        : null;
      const config = bms.series_configuration || null;
      const chem = bms.supported_chemistry || null;

      return [
        { label: "Max Discharge", value: maxA || "—" },
        { label: "Peak Current", value: peakA || "—" },
        { label: "Configuration", value: config || "—" },
        { label: "Chemistry", value: chem || "LiFePO4" },
      ];
    }
    case "charger": {
      const chg =
        parsedSpecs?.charger_specifications ||
        parsedSpecs?.electrical_specifications ||
        {};
      const voltMatch = name?.match(/(\d+(\.\d+)?)\s*(v|volt)/i);
      const ampMatch = name?.match(/(\d+(\.\d+)?)\s*(a|amp)/i);

      const v = voltMatch
        ? `${voltMatch[1]}V`
        : chg.output_voltage_v
          ? `${chg.output_voltage_v}V`
          : "—";
      const a = ampMatch
        ? `${ampMatch[1]}A`
        : chg.max_charging_current_a
          ? `${chg.max_charging_current_a}A`
          : "—";
      const chem = chg.supported_chemistry || "LiFePO4 / Li-ion";

      return [
        { label: "Output Voltage", value: v },
        { label: "Max Current", value: a },
        { label: "Supported", value: chem },
        { label: "Warranty", value: warranty || "1 Year" },
      ];
    }
    default:
      return null;
  }
};

/* ─────────────────────────────────────────────────────────────
   SAFE JSON PARSER
   ───────────────────────────────────────────────────────────── */
const parseTechnicalSpecs = (technicalSpecs) => {
  if (!technicalSpecs) return null;

  if (typeof technicalSpecs === "object" && !Array.isArray(technicalSpecs)) {
    if (typeof technicalSpecs.description === "string") {
      try {
        return JSON.parse(technicalSpecs.description);
      } catch (_) {}
    }
    const knownKeys = [
      "category",
      "battery_specifications",
      "bms_specifications",
      "electrical_specifications",
      "inverter_specifications",
      "integrated_battery_specifications",
      "protection_parameters",
      "balancing_specifications",
      "connectivity_and_sensors",
      "charger_specifications",
      "salient_features",
      "warranty",
      "product_description",
      "application",
    ];
    if (knownKeys.some((k) => k in technicalSpecs)) return technicalSpecs;
    if (typeof technicalSpecs.specs === "string") {
      try {
        return JSON.parse(technicalSpecs.specs);
      } catch (_) {}
    }
  }

  if (typeof technicalSpecs === "string") {
    try {
      return JSON.parse(technicalSpecs);
    } catch (err) {
      console.error("Failed to parse technical specs string", err);
      return null;
    }
  }

  return null;
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const ProductDetailPage = ({
  onAddToCart,
  onNavigate,
  cartItems = [],
  onQuantityChange,
  onRemoveItem,
}) => {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [parsedSpecs, setParsedSpecs] = useState(null);
  const [productType, setProductType] = useState("unknown");
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [pincode, setPincode] = useState("");
  const [pincodeStatus, setPincodeStatus] = useState("");
  const [isEkartServiceable, setIsEkartServiceable] = useState(null);
  const [ekartEstimatedDate, setEkartEstimatedDate] = useState("");

  const [emiPlans, setEmiPlans] = useState(null);

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showCartSuccess, setShowCartSuccess] = useState(false);
  const [isBuyingNow, setIsBuyingNow] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [swingAnim, setSwingAnim] = useState(false);
  const [viewerCount, setViewerCount] = useState(16);
  const [couponCopied, setCouponCopied] = useState(false);

  const [notifyStatus, setNotifyStatus] = useState("idle");

  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const reviewsRef = useRef(null);
  const weight = product?.product_weight || 0.5;

  useEffect(() => {
    const fetchEmiData = async () => {
      try {
        const RAZORPAY_KEY_ID =
          process.env.REACT_APP_RAZORPAY_KEY_ID || "YOUR_RAZORPAY_KEY_ID";
        const basicAuthHash = btoa(`${RAZORPAY_KEY_ID}:`);

        const response = await fetch("https://api.razorpay.com/v1/methods", {
          method: "GET",
          headers: {
            Authorization: `Basic ${basicAuthHash}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          setEmiPlans(data.emi_banks || data.emi);
        }
      } catch (err) {
        console.error("Failed to fetch Razorpay EMI plans:", err);
      }
    };

    fetchEmiData();
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (loading || !product) return;
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: "0px", threshold: 0.15 },
    );
    setTimeout(() => {
      document
        .querySelectorAll(".scroll-reveal")
        .forEach((el) => observer.observe(el));
    }, 100);
    return () => observer.disconnect();
  }, [loading, product, activeTab]);

  const nextImage = () => {
    if (!product?.images?.length) return;
    setActiveImageIndex((prev) =>
      prev === product.images.length - 1 ? 0 : prev + 1,
    );
  };
  const prevImage = () => {
    if (!product?.images?.length) return;
    setActiveImageIndex((prev) =>
      prev === 0 ? product.images.length - 1 : prev - 1,
    );
  };
  const selectImage = (index) => setActiveImageIndex(index);

  const handleImageMouseMove = (e) => {
    const { left, top, width, height } =
      e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setZoomPos({ x, y });
  };

  /* ── ✅ UPDATED: Ekart Pincode Logic with Postal API Integration ── */
  const handlePincodeCheck = async () => {
    if (!/^[0-9]{6}$/.test(pincode)) {
      setPincodeStatus("❌ Please enter a valid 6-digit pincode");
      setIsEkartServiceable(false);
      return;
    }

    try {
      setPincodeStatus("⏳ Verifying serviceability...");
      setIsEkartServiceable(null);

      const result = await checkPincodeService(pincode, weight);

      if (!result.success) {
        setPincodeStatus(`❌ ${result.message || "Pincode not serviceable"}`);
        setIsEkartServiceable(false);
        return;
      }

      setIsEkartServiceable(true);

      const deliveryDate = new Date();
      deliveryDate.setDate(
        deliveryDate.getDate() + (result.estimatedDays || 4),
      );

      setEkartEstimatedDate(
        deliveryDate.toLocaleDateString("en-IN", {
          weekday: "short",
          month: "short",
          day: "numeric",
        }),
      );

      const locationText =
        result.district && result.state
          ? ` — ${result.district}, ${result.state}`
          : "";

      setPincodeStatus(`🚚 Delivery available to ${pincode}${locationText}`);

      localStorage.setItem("pincode", pincode);
    } catch (error) {
      console.error(error);
      setPincodeStatus("❌ Something went wrong verifying serviceability.");
      setIsEkartServiceable(false);
    }
  };
  const handleCopyCoupon = () => {
    const code = "WELCOME10";
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code).then(() => {
        setCouponCopied(true);
        setTimeout(() => setCouponCopied(false), 2000);
      });
    } else {
      const ta = document.createElement("textarea");
      ta.value = code;
      ta.style.position = "absolute";
      ta.style.left = "-999999px";
      document.body.prepend(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCouponCopied(true);
        setTimeout(() => setCouponCopied(false), 2000);
      } catch (e) {
        console.error(e);
      } finally {
        ta.remove();
      }
    }
  };

  const scrollToReviews = () => {
    if (reviewsRef.current)
      reviewsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    setViewerCount(Math.floor(Math.random() * 25) + 12);
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await productService.getProductById(productId);
        setProduct(data);
        const specs = parseTechnicalSpecs(data.technical_specs);
        setParsedSpecs(specs);
        setProductType(detectProductType(specs, data.name));
        try {
          const all = await productService.getAllProducts();
          setRelatedProducts(all.filter((p) => p._id !== data._id).slice(0, 4));
        } catch (e) {
          console.error("Related products failed", e);
        }
        setActiveImageIndex(0);
      } catch (err) {
        console.error(err);
        setError("Failed to load product details");
      } finally {
        setLoading(false);
      }
    };
    if (productId) fetchProduct();
  }, [productId]);

  if (loading) return <p className="page-loader">Loading product...</p>;
  if (error) return <p className="error-text">{error}</p>;
  if (!product) return <p>Product not found</p>;

  const {
    name,
    description,
    images = [],
    old_price = 0,
    price_display,
    stock_level,
    reviews = [],
  } = product;

  const isOutOfStock = stock_level <= 0;
  const snapshotItems = getSnapshotItems(productType, parsedSpecs, name);
  const currentId = product?.product_id || product?._id;
  const cartItem = cartItems.find(
    (item) => item.productId === currentId || item.id === currentId,
  );
  const quantity = cartItem ? cartItem.quantity : 0;

  const handleAddClick = () => {
    if (isOutOfStock) return;
    setIsAddingToCart(true);
    setTimeout(async () => {
      await onAddToCart(product);
      setIsAddingToCart(false);
      setSwingAnim(true);
      setTimeout(() => setSwingAnim(false), 500);
      setShowCartSuccess(true);
      setTimeout(() => setShowCartSuccess(false), 2500);
    }, 800);
  };

  const handleIncrease = (e) => {
    e.stopPropagation();
    if (cartItem && onQuantityChange && !isOutOfStock) {
      setIsAddingToCart(true);
      setTimeout(async () => {
        await onQuantityChange(cartItem.cartItemId, quantity + 1);
        setIsAddingToCart(false);
        setSwingAnim(true);
        setTimeout(() => setSwingAnim(false), 500);
      }, 500);
    }
  };

  const handleDecrease = (e) => {
    e.stopPropagation();
    if (cartItem) {
      setIsAddingToCart(true);
      setTimeout(async () => {
        if (quantity === 1 && onRemoveItem)
          await onRemoveItem(cartItem.cartItemId);
        else if (onQuantityChange)
          await onQuantityChange(cartItem.cartItemId, quantity - 1);
        setIsAddingToCart(false);
      }, 500);
    }
  };

  const handleNotifyMeClick = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      navigate("/auth");
      return;
    }
    const userEmail = currentUser.email;
    if (!userEmail) {
      alert(
        "Please update your profile with an email address to receive notifications.",
      );
      return;
    }

    setNotifyStatus("loading");
    try {
      await productService.requestRestockNotification({
        productId: currentId,
        productName: name,
        email: userEmail,
      });
      setNotifyStatus("success");
    } catch (err) {
      console.error("Notify failed:", err);
      setNotifyStatus("error");
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="tab-content-section scroll-reveal slide-up">
            <h4>Product Overview</h4>
            <p>{description || parsedSpecs?.product_description}</p>
            {parsedSpecs?.application && (
              <>
                <h4>Applications</h4>
                <ul>
                  {parsedSpecs.application.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </>
            )}
            {parsedSpecs?.salient_features && (
              <>
                <h4>Key Features</h4>
                <ul>
                  {parsedSpecs.salient_features.map((f, i) => (
                    <li key={i}>✔ {f}</li>
                  ))}
                </ul>
              </>
            )}

            {productType === "battery" && (
              <>
                <h4>What's in the Box</h4>
                <ul>
                  <li>✔ Lithium Battery Pack</li>
                  <li>✔ Pre-attached connector wires</li>
                  <li>✔ Safety protection circuit</li>
                </ul>
              </>
            )}
            {productType === "bms" && (
              <>
                <h4>What's in the Box</h4>
                <ul>
                  <li>✔ BMS Board</li>
                  <li>✔ Balance wire harness</li>
                  <li>✔ Temperature sensor probes</li>
                  <li>✔ Wiring diagram</li>
                </ul>
              </>
            )}

            {(productType === "inverter" ||
              productType === "integrated_inverter") && (
              <>
                <h4>What's in the Box</h4>
                <ul>
                  <li>✔ Zynventics Inverter Unit</li>
                  <li>✔ Power input cable</li>
                  <li>✔ User manual</li>
                  {productType === "integrated_inverter" && (
                    <li>✔ Wheels &amp; handle assembly</li>
                  )}
                </ul>
              </>
            )}
            {productType === "charger" && (
              <>
                <h4>What's in the Box</h4>
                <ul>
                  <li>✔ Smart Battery Charger Unit</li>
                  <li>✔ AC Input Power Cord</li>
                  <li>✔ DC Output cables with clamp connectors</li>
                  <li>✔ Instruction & Safety Manual</li>
                </ul>
              </>
            )}

            {productType === "charger" && (
              <ChargerShowcase
                productImage={images.length > 0 ? images[0] : undefined}
                specs={parsedSpecs}
              />
            )}
          </div>
        );
      case "specs":
        if (!parsedSpecs)
          return (
            <div className="tab-content-section">
              <p className="no-specs-text">
                Technical specifications are not yet available for this product.
                Please contact our team for detailed spec sheets.
              </p>
            </div>
          );
        return (
          <div className="tab-content-section scroll-reveal slide-up">
            {(() => {
              const SpecTable = ({ title, data }) => {
                if (!data || typeof data !== "object" || Array.isArray(data))
                  return null;
                const entries = Object.entries(data);
                if (entries.length === 0) return null;
                return (
                  <div className="specs-group">
                    <h4>{title}</h4>
                    <table className="specs-table-content">
                      <tbody>
                        {entries.map(([k, v]) => (
                          <tr key={k}>
                            <th style={{ textTransform: "capitalize" }}>
                              {k.replaceAll("_", " ")}
                            </th>
                            <td>
                              {Array.isArray(v)
                                ? v.join(", ")
                                : typeof v === "object"
                                  ? JSON.stringify(v)
                                  : String(v)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              };
              const SpecList = ({ title, items }) => {
                if (!items?.length) return null;
                return (
                  <div className="specs-group">
                    <h4>{title}</h4>
                    <ul>
                      {items.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                );
              };

              if (productType === "battery")
                return (
                  <>
                    <SpecTable
                      title="Battery Specifications"
                      data={parsedSpecs.battery_specifications}
                    />
                    <SpecTable
                      title="BMS Specifications"
                      data={parsedSpecs.bms_specifications}
                    />
                    <SpecTable
                      title="Physical Specifications"
                      data={parsedSpecs.physical_specifications}
                    />
                    <SpecTable
                      title="Operating Conditions"
                      data={parsedSpecs.operating_conditions}
                    />
                    <SpecList
                      title="Key Features"
                      items={parsedSpecs.salient_features}
                    />
                  </>
                );
              if (productType === "inverter")
                return (
                  <>
                    <SpecTable
                      title="Electrical Specifications"
                      data={parsedSpecs.electrical_specifications}
                    />
                    <SpecTable
                      title="Battery Compatibility"
                      data={parsedSpecs.battery_compatibility}
                    />
                    <SpecTable
                      title="Physical Specifications"
                      data={parsedSpecs.physical_specifications}
                    />
                    <SpecTable
                      title="Operating Conditions"
                      data={parsedSpecs.operating_conditions}
                    />
                    {parsedSpecs.safety_and_protection?.safe_operations && (
                      <SpecList
                        title="Safety & Protection"
                        items={
                          parsedSpecs.safety_and_protection.safe_operations
                        }
                      />
                    )}
                    <SpecList
                      title="Key Features"
                      items={parsedSpecs.salient_features}
                    />
                  </>
                );
              if (productType === "integrated_inverter")
                return (
                  <>
                    <SpecTable
                      title="Inverter Specifications"
                      data={parsedSpecs.inverter_specifications}
                    />
                    <SpecTable
                      title="Integrated Battery"
                      data={parsedSpecs.integrated_battery_specifications}
                    />
                    <SpecTable
                      title="Charging Specifications"
                      data={parsedSpecs.charging_specifications}
                    />
                    <SpecTable
                      title="BMS & Protections"
                      data={parsedSpecs.internal_bms_protections}
                    />
                    <SpecTable
                      title="Physical Specifications"
                      data={parsedSpecs.physical_specifications}
                    />
                    <SpecTable
                      title="Operating Conditions"
                      data={parsedSpecs.operating_conditions}
                    />
                    <SpecList
                      title="Key Features"
                      items={parsedSpecs.salient_features}
                    />
                  </>
                );
              if (productType === "bms")
                return (
                  <>
                    <SpecTable
                      title="BMS Specifications"
                      data={parsedSpecs.bms_specifications}
                    />
                    <SpecTable
                      title="Protection Parameters"
                      data={parsedSpecs.protection_parameters}
                    />
                    <SpecTable
                      title="Balancing Specifications"
                      data={parsedSpecs.balancing_specifications}
                    />
                    <SpecTable
                      title="Connectivity & Sensors"
                      data={parsedSpecs.connectivity_and_sensors}
                    />
                    <SpecTable
                      title="Physical Specifications"
                      data={parsedSpecs.physical_specifications}
                    />
                    <SpecList
                      title="Key Features"
                      items={parsedSpecs.salient_features}
                    />
                  </>
                );
              if (productType === "charger")
                return (
                  <>
                    <SpecTable
                      title="Charger Specifications"
                      data={
                        parsedSpecs.charger_specifications ||
                        parsedSpecs.electrical_specifications
                      }
                    />
                    <SpecTable
                      title="Protection & Safety"
                      data={
                        parsedSpecs.protection_parameters ||
                        parsedSpecs.safety_and_protection
                      }
                    />
                    <SpecTable
                      title="Physical Specifications"
                      data={parsedSpecs.physical_specifications}
                    />
                    <SpecList
                      title="Key Features"
                      items={parsedSpecs.salient_features}
                    />
                  </>
                );

              return (
                <>
                  <SpecTable
                    title="General Specifications"
                    data={
                      parsedSpecs.electrical_specifications ||
                      parsedSpecs.battery_specifications
                    }
                  />
                  <SpecTable
                    title="Physical Specifications"
                    data={parsedSpecs.physical_specifications}
                  />
                  <SpecTable
                    title="Operating Conditions"
                    data={parsedSpecs.operating_conditions}
                  />
                  <SpecList
                    title="Key Features"
                    items={parsedSpecs.salient_features}
                  />
                </>
              );
            })()}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="product-detail-page section-reveal visible">
      <div className="product-header-grid">
        <div className="image-gallery-container scroll-reveal slide-right">
          <div
            className="main-image-wrapper"
            onMouseEnter={() => setIsZoomed(true)}
            onMouseLeave={() => setIsZoomed(false)}
            onMouseMove={handleImageMouseMove}
            onClick={() => setIsModalOpen(true)}
            style={{ cursor: "crosshair" }}
          >
            {images.length > 1 && (
              <button
                className="slider-btn prev"
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
              >
                ❮
              </button>
            )}

            <img
              src={images[activeImageIndex] || FALLBACK_IMAGE}
              alt={name}
              className={`main-product-image ${isOutOfStock ? "pc-image-grayscale" : ""} ${isZoomed ? "zoomed" : ""}`}
              onError={(e) => (e.target.src = FALLBACK_IMAGE)}
              style={{
                ...(isOutOfStock
                  ? { filter: "grayscale(100%)", opacity: 0.7 }
                  : {}),
                ...(isZoomed
                  ? { transformOrigin: `${zoomPos.x}% ${zoomPos.y}%` }
                  : {}),
              }}
            />

            {isOutOfStock && (
              <div
                className="pc-oos-overlay"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.75)",
                  color: "white",
                  padding: "8px 16px",
                  fontWeight: "bold",
                  zIndex: 5,
                  borderRadius: "4px",
                }}
              >
                OUT OF STOCK
              </div>
            )}

            {images.length > 1 && (
              <button
                className="slider-btn next"
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
              >
                ❯
              </button>
            )}
          </div>

          {images.length > 1 && (
            <div className="thumbnail-strip">
              {images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`Thumbnail ${i + 1}`}
                  onPointerEnter={(e) => {
                    if (e.pointerType === "mouse") {
                      selectImage(i);
                    }
                  }}
                  onClick={() => {
                    selectImage(i);
                  }}
                  className={`thumbnail-image ${activeImageIndex === i ? "active-thumb" : ""}`}
                  style={{ cursor: "zoom-in" }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="product-details-container scroll-reveal slide-left">
          <h1 className="product-title">{name}</h1>
          <hr className="divider" />

          <div className="watching-banner">
            <span className="watching-icon">👁️</span>
            <span>{viewerCount} People watching this product now!</span>
          </div>

          <div className="price-stock-row">
            <div className="price-section">
              {old_price > price_display && (
                <div className="old-price-row">
                  <span className="old-price">₹{old_price}</span>
                  <span className="discount-badge">
                    {Math.round(
                      ((old_price - price_display) / old_price) * 100,
                    )}
                    % OFF
                  </span>
                </div>
              )}
              <p className="price-display">₹{price_display}</p>
              <span className="gst-text">(inclusive of GST)</span>
              <div
                className="rating-summary-row"
                onClick={scrollToReviews}
                style={{ cursor: "pointer" }}
              >
                <div className="stars">
                  <span className="star filled">★</span>
                  <span className="star filled">★</span>
                  <span className="star filled">★</span>
                  <span className="star filled">★</span>
                  <span className="star half-filled">★</span>
                </div>
                <span className="review-link">{reviews.length} Reviews</span>
              </div>
              {old_price > price_display && (
                <p className="save-text" style={{ marginTop: "12px" }}>
                  You save ₹{old_price - price_display}
                </p>
              )}
            </div>
            {stock_level > 0 && (
              <div className="stock-pill">
                <span className="stock-dot"></span>
                <span>{stock_level} in stock</span>
              </div>
            )}
          </div>

          <p
            className={`stock-status ${stock_level > 0 ? "in-stock" : "out-of-stock"}`}
          >
            {stock_level > 0 ? "In Stock - Ready to Ship" : "Out of Stock"}
          </p>

          <RazorpayEmiWidget amount={price_display} emiPlans={emiPlans} />

          <div
            className={`discount-coupon-block ${couponCopied ? "applied" : ""}`}
            onClick={handleCopyCoupon}
            style={{ cursor: "pointer", transition: "all 0.3s ease" }}
          >
            <div className="discount-left">
              <span className="discount-icon">
                {couponCopied ? "✅" : "🏷️"}
              </span>
              <div className="discount-info">
                <h4>
                  {couponCopied
                    ? "Coupon Code Copied!"
                    : "Zynventics Discount for New Customers"}
                </h4>
                <p>
                  {couponCopied
                    ? "You can apply this at checkout."
                    : "Hurry and get discounts on all Zynventics products up to 10%"}
                </p>
                <p className="use-code-text" style={{ marginTop: "8px" }}>
                  <strong>
                    {couponCopied
                      ? "Ready to use!"
                      : "Click to copy code WELCOME10"}
                  </strong>
                </p>
              </div>
            </div>
            <div className="discount-right">
              <span
                className={`discount-badge-code ${couponCopied ? "success" : ""}`}
              >
                {couponCopied ? "COPIED" : "WELCOME10"}
              </span>
            </div>
          </div>

          <div className="product-highlights">
            <ul>
              {productType === "battery" && (
                <>
                  <li>✔ Long backup &amp; high efficiency</li>
                  <li>✔ Lightweight lithium battery</li>
                  <li>✔ Built-in protection &amp; safety circuit</li>
                  <li>✔ Ideal for portable &amp; inverter use</li>
                </>
              )}
              {productType === "inverter" && (
                <>
                  <li>✔ Pure sine wave — safe for all appliances</li>
                  <li>✔ &lt;10ms transfer time — no computer restart</li>
                  <li>✔ Dedicated lithium charging profile</li>
                  <li>✔ DSP-controlled thermal management</li>
                </>
              )}
              {productType === "integrated_inverter" && (
                <>
                  <li>✔ All-in-one plug &amp; play — zero exposed wiring</li>
                  <li>✔ Integrated LiFePO4 — 3× life of tubular</li>
                  <li>✔ Compact — saves 60% space</li>
                  <li>✔ &lt;10ms auto-switch — routers never restart</li>
                </>
              )}
              {productType === "bms" && (
                <>
                  <li>✔ Active cell balancing — extends pack life</li>
                  <li>✔ Bluetooth monitoring via smartphone app</li>
                  <li>✔ Dual temperature sensors</li>
                  <li>✔ Conformal silicone coating</li>
                </>
              )}
              {productType === "charger" && (
                <>
                  <li>✔ Smart multi-stage charging profile</li>
                  <li>✔ Auto-cut off logic prevents overcharging</li>
                  <li>✔ Active fan cooling for thermal stability</li>
                  <li>
                    ✔ Optimized specifically for LiFePO4 / Li-ion chemistries
                  </li>
                </>
              )}
              {productType === "unknown" && (
                <>
                  <li>✔ Long backup &amp; high efficiency</li>
                  <li>✔ Built-in protection &amp; safety circuit</li>
                  <li>✔ Premium quality components</li>
                  <li>✔ Zynventics engineered reliability</li>
                </>
              )}
            </ul>
          </div>

          <div className="pincode-checker">
            <p
              style={{
                margin: "0 0 8px 0",
                fontSize: "14px",
                fontWeight: "600",
              }}
            >
              Check Ekart Serviceability
            </p>
            <div style={{ display: "flex", gap: "10px" }}>
              <input
                type="text"
                placeholder="Enter delivery pincode"
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
              />
              <button
                onClick={handlePincodeCheck}
                disabled={pincodeStatus === "⏳ Verifying serviceability..."}
              >
                {pincodeStatus === "⏳ Verifying serviceability..."
                  ? "Checking..."
                  : "Check"}
              </button>
            </div>
            {/* The status now successfully appends the location fetched from Postal API */}
            {pincodeStatus && <p className="pincode-status">{pincodeStatus}</p>}
          </div>

          <div
            className={`buying-controls ${swingAnim ? "swing-animation" : ""}`}
            style={{
              position: "relative",
              gap: "12px",
              display: "flex",
              width: "100%",
            }}
          >
            {isAddingToCart && (
              <div
                className="adding-overlay"
                style={{
                  borderRadius: "8px",
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(255,255,255,0.7)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                }}
              >
                <div
                  className="spinner"
                  style={{ width: "30px", height: "30px", borderWidth: "3px" }}
                ></div>
              </div>
            )}

            {isOutOfStock ? (
              <button
                className={`mobile-buy-btn notify-action-btn ${
                  notifyStatus === "loading"
                    ? "notify-loading"
                    : notifyStatus === "success"
                      ? "notify-success"
                      : "notify-default"
                }`}
                disabled={
                  notifyStatus === "loading" || notifyStatus === "success"
                }
                onClick={handleNotifyMeClick}
                style={{ height: "100%" }}
              >
                {notifyStatus === "loading" ? (
                  "Wait..."
                ) : notifyStatus === "success" ? (
                  <>
                    <span className="custom-check-icon">✓</span>
                    We will notify you!
                  </>
                ) : (
                  <>
                    <span className="custom-bell-icon">🔔</span>
                    Notify Me
                  </>
                )}
              </button>
            ) : quantity > 0 ? (
              <div
                className="quantity-selector-btn"
                style={{
                  borderRadius: "8px",
                  flex: 1,
                  margin: 0,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "0 12px",
                }}
              >
                <button className="qty-btn" onClick={handleDecrease}>
                  -
                </button>
                <span className="qty-value">{quantity}</span>
                <button className="qty-btn" onClick={handleIncrease}>
                  +
                </button>
              </div>
            ) : (
              <button
                className="add-to-cart-btn"
                onClick={handleAddClick}
                style={{ borderRadius: "8px", flex: 1, margin: 0 }}
              >
                <CartIcon /> Add to Cart
              </button>
            )}

            {!isOutOfStock && (
              <button
                className="buy-now-btn"
                style={{ flex: 1 }}
                onClick={async () => {
                  try {
                    setIsBuyingNow(true);
                    if (quantity === 0) await onAddToCart(product);
                    setTimeout(() => onNavigate("cart"), 600);
                  } catch (err) {
                    setIsBuyingNow(false);
                  }
                }}
              >
                Buy Now
              </button>
            )}
          </div>

          <div className="payment-trust-box">
            <p className="trust-title">Guaranteed Safe Checkout</p>
            <div className="secure-badge-row">
              <span className="secure-lock">🔒</span>
              <span>100% Secure Payments</span>
            </div>
            <div className="payment-icons">
              <img src="/payments/upi.svg" alt="UPI" />
              <img src="/payments/visa.svg" alt="Visa" />
              <img src="/payments/mastercard.svg" alt="Mastercard" />
              <img src="/payments/rupay.svg" alt="RuPay" />
            </div>
            <p className="trust-subtext">
              All transactions are encrypted and secured by Razorpay.
            </p>
          </div>

          <div className="couriers-section scroll-reveal slide-up">
            {isEkartServiceable !== null && (
              <div
                className="courier-row"
                style={{
                  border: isEkartServiceable
                    ? "2px solid #38bdf8"
                    : "2px solid #ef4444",
                  backgroundColor: isEkartServiceable ? "#f0f9ff" : "#fef2f2",
                }}
              >
                <div className="courier-info">
                  <div
                    className="courier-logo"
                    style={{
                      background: "#0ea5e9",
                      color: "#fff",
                      fontSize: "12px",
                      padding: "8px",
                    }}
                  >
                    <strong>EKART</strong>
                  </div>
                  <div className="courier-text">
                    <strong>
                      {isEkartServiceable
                        ? "Ekart Logistics Available"
                        : "Ekart Not Available"}
                    </strong>
                    <p>
                      {isEkartServiceable
                        ? `Fast delivery confirmed for pincode ${pincode}`
                        : `Cannot deliver to ${pincode}`}
                    </p>
                  </div>
                </div>
                {isEkartServiceable && (
                  <div className="courier-meta">
                    <span className="courier-time">
                      Est. {ekartEstimatedDate}
                    </span>
                    <span className="courier-price">Standard</span>
                  </div>
                )}
              </div>
            )}

            <div className="courier-row">
              <div className="courier-info">
                <div
                  className="courier-logo"
                  style={{
                    background: "#0f172a",
                    color: "#fff",
                    fontSize: "10px",
                  }}
                >
                  <strong>EKART</strong>
                </div>
                <div className="courier-text">
                  <strong>Ekart Surface Logistics</strong>
                  <p>
                    {productType === "battery" ||
                    productType === "integrated_inverter"
                      ? "Mandatory surface shipping for high-capacity lithium units."
                      : "Our official shipping partner for reliable delivery."}
                  </p>
                </div>
              </div>
              <div className="courier-meta">
                <span className="courier-time">3-7 Days</span>
                <span className="courier-price">Free</span>
              </div>
            </div>

            {productType === "charger" || productType === "bms" ? (
              <div className="courier-row fast-delivery-row">
                <div className="courier-info">
                  <div
                    className="courier-logo"
                    style={{
                      background: "#e0e7ff",
                      color: "#4f46e5",
                      fontSize: "1.4rem",
                    }}
                  >
                    ✈️
                  </div>
                  <div className="courier-text">
                    <strong>Ekart Express Air</strong>
                    <p>Fast track delivery via air freight.</p>
                  </div>
                </div>
                <div className="courier-meta fast-meta">
                  <div className="fast-meta-top">
                    <span className="courier-time">1-3 Days</span>
                    <span className="courier-price chargeable">Chargeable</span>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="courier-row fast-delivery-row"
                style={{ backgroundColor: "#fef3c7", borderColor: "#fde047" }}
              >
                <div className="courier-info">
                  <div
                    className="courier-logo"
                    style={{
                      background: "#fef08a",
                      color: "#854d0e",
                      fontSize: "1.2rem",
                    }}
                  >
                    ⚠️
                  </div>
                  <div className="courier-text">
                    <strong>Aviation Safety Notice</strong>
                    <p style={{ color: "#854d0e", fontSize: "12px" }}>
                      Air freight is prohibited for high-capacity lithium
                      batteries.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {snapshotItems !== null && (
        <div className="technical-snapshot scroll-reveal slide-up">
          {snapshotItems.map((item) => (
            <div key={item.label}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          ))}
        </div>
      )}

      <div className="product-tabs-container scroll-reveal slide-up delay-100">
        <div className="tabs-nav">
          {["overview", "specs"].map((tab) => (
            <button
              key={tab}
              className={`tab-button ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="tabs-content">{renderTabContent()}</div>
      </div>

      {productType === "battery" && (
        <BatteryShowcase
          productImage={images.length > 0 ? images[0] : undefined}
          specs={parsedSpecs}
        />
      )}

      {(productType === "inverter" ||
        productType === "integrated_inverter") && (
        <>
          <InverterShowcase
            productImage={images.length > 0 ? images[0] : undefined}
            specs={parsedSpecs}
            isIntegrated={productType === "integrated_inverter"}
          />
          <BatteryLifeComparison
            productImage={images.length > 0 ? images[0] : undefined}
          />
          <InverterComparison
            productImage={images.length > 0 ? images[0] : undefined}
          />
          <InverterDisplayModes />
          <InverterInterface />
          <BackupChart
            capacityVa={parsedSpecs?.electrical_specifications?.capacity_va}
          />
          <SolarReady />
        </>
      )}

      {productType === "bms" && (
        <BMSShowcase
          productImage={images.length > 0 ? images[0] : undefined}
          specs={parsedSpecs}
        />
      )}

      <ZynvertEdge />

      {(productType === "battery" || productType === "unknown") && (
        <div className="comparison-section scroll-reveal zoom-in">
          <h3>Why Upgrade to Zynventics Lithium?</h3>
          <div className="comparison-grid-visual">
            <div className="compare-card lithium-card">
              <div className="compare-badge winner">Recommended</div>
              <div className="compare-img-wrapper">
                <img
                  src={images.length > 0 ? images[0] : FALLBACK_IMAGE}
                  alt="Zynvert Lithium Battery"
                  className="compare-img"
                />
              </div>
              <h4 className="compare-title">Zynventics Lithium</h4>
              <ul className="compare-features">
                {[
                  ["Lightweight", "Easy to handle & transport"],
                  ["Longer Backup", "Consistent voltage & power"],
                  ["2000+ Cycles", "Lasts 5 to 7 years easily"],
                  ["Maintenance Free", "Zero acid or water topping"],
                  ["95–98% Efficiency", "Super fast charging times"],
                ].map(([t, s]) => (
                  <li key={t}>
                    <span className="icon success">✔</span>
                    <div className="feature-text">
                      <strong>{t}</strong>
                      <span>{s}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="vs-divider">
              <span>VS</span>
            </div>
            <div className="compare-card lead-acid-card">
              <div className="compare-badge outdated">Outdated</div>
              <div className="compare-img-wrapper">
                <img
                  src="https://res.cloudinary.com/dhbjxspxa/image/upload/v1777475751/products/Lead-Acid-Battery_new_ybhym4.jpg"
                  alt="Lead Acid Battery"
                  className="compare-img"
                  style={{ borderRadius: "8px" }}
                />
              </div>
              <h4 className="compare-title">Lead Acid Battery</h4>
              <ul className="compare-features">
                {[
                  ["Very Heavy", "Hard to move or install"],
                  ["Shorter Backup", "Voltage drops quickly"],
                  ["300–500 Cycles", "Needs frequent replacement"],
                  ["High Maintenance", "Needs distilled water refills"],
                  ["70–80% Efficiency", "Slow and wastes electricity"],
                ].map(([t, s]) => (
                  <li key={t}>
                    <span className="icon error">✖</span>
                    <div className="feature-text">
                      <strong>{t}</strong>
                      <span>{s}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="zynvert-features-section scroll-reveal slide-up">
        <h3>Why Choose Zynventics?</h3>
        <div className="features-grid">
          {productType === "bms" ? (
            <>
              <div className="feature-card scroll-reveal slide-up delay-100">
                <span className="feature-icon">📱</span>
                <h4>Bluetooth Monitoring</h4>
                <p>
                  Monitor cell voltages, SOC, temperature, and faults live from
                  your phone.
                </p>
              </div>
              <div className="feature-card scroll-reveal slide-up delay-200">
                <span className="feature-icon">⚖️</span>
                <h4>Active Balancing</h4>
                <p>
                  Keeps every cell at the same voltage — maximises capacity and
                  pack life.
                </p>
              </div>
              <div className="feature-card scroll-reveal slide-up delay-300">
                <span className="feature-icon">🌡️</span>
                <h4>Dual Temperature Sensors</h4>
                <p>
                  Independent cell and PCB thermal monitoring. Cuts power before
                  damage occurs.
                </p>
              </div>
              <div className="feature-card scroll-reveal slide-up delay-400">
                <span className="feature-icon">💧</span>
                <h4>Conformal Coating</h4>
                <p>
                  Silicone coated PCB survives humidity, dust, and real-world
                  installation environments.
                </p>
              </div>
            </>
          ) : productType === "inverter" ||
            productType === "integrated_inverter" ? (
            <>
              <div className="feature-card scroll-reveal slide-up delay-100">
                <span className="feature-icon">〰️</span>
                <h4>Pure Sine Wave</h4>
                <p>
                  Grid-quality waveform protects sensitive electronics and runs
                  appliances silently.
                </p>
              </div>
              <div className="feature-card scroll-reveal slide-up delay-200">
                <span className="feature-icon">⚡</span>
                <h4>Instant Auto-Switch</h4>
                <p>
                  &lt;10ms transfer — your computers and routers never know the
                  power went out.
                </p>
              </div>
              <div className="feature-card scroll-reveal slide-up delay-300">
                <span className="feature-icon">🧠</span>
                <h4>DSP Intelligence</h4>
                <p>
                  Digital Signal Processor manages power routing, charging, and
                  thermals in real time.
                </p>
              </div>
              <div className="feature-card scroll-reveal slide-up delay-400">
                <span className="feature-icon">🔋</span>
                <h4>Lithium-First Design</h4>
                <p>
                  Built from the ground up to get the most from your Zynventics
                  LiFePO4 battery.
                </p>
              </div>
            </>
          ) : productType === "charger" ? (
            <>
              <div className="feature-card scroll-reveal slide-up delay-100">
                <span className="feature-icon">🔌</span>
                <h4>Smart Charging Profile</h4>
                <p>
                  Multi-stage logic ensures your lithium cells are charged
                  safely to maximum capacity without over-voltage.
                </p>
              </div>
              <div className="feature-card scroll-reveal slide-up delay-200">
                <span className="feature-icon">🛑</span>
                <h4>Auto Cut-Off</h4>
                <p>
                  The charger actively detects a full battery state and stops
                  current flow to prevent overcharging degradation.
                </p>
              </div>
              <div className="feature-card scroll-reveal slide-up delay-300">
                <span className="feature-icon">❄️</span>
                <h4>Active Cooling System</h4>
                <p>
                  Integrated thermal management keeps the charger cool during
                  long, high-current charging sessions.
                </p>
              </div>
              <div className="feature-card scroll-reveal slide-up delay-400">
                <span className="feature-icon">🛡️</span>
                <h4>Multi-Protection Safety</h4>
                <p>
                  Built-in protection against reverse polarity, short-circuit,
                  and sudden AC voltage spikes.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="feature-card scroll-reveal slide-up delay-100">
                <span className="feature-icon">🥇</span>
                <h4>BIS Certified Cells</h4>
                <p>
                  100% genuine, BIS-certified cells — authentic capacity,
                  supreme quality, peace of mind.
                </p>
              </div>
              <div className="feature-card scroll-reveal slide-up delay-200">
                <span className="feature-icon">⏳</span>
                <h4>Long Backup Time</h4>
                <p>
                  High energy density keeps your home and office running longer
                  during power cuts.
                </p>
              </div>
              <div className="feature-card scroll-reveal slide-up delay-300">
                <span className="feature-icon">🛡️</span>
                <h4>Ultimate Safety</h4>
                <p>
                  Smart BMS protects against overcharging, deep-discharging, and
                  short circuits.
                </p>
              </div>
              <div className="feature-card scroll-reveal slide-up delay-400">
                <span className="feature-icon">🔄</span>
                <h4>Long Lifecycles</h4>
                <p>
                  2000+ charge cycles — lasts up to 5× longer than traditional
                  lead-acid batteries.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="applications-section scroll-reveal zoom-in delay-100">
        <h3>Power Anything. Anywhere.</h3>
        <p className="section-subtitle">
          {productType === "bms"
            ? "Zynvert BMS is engineered for diverse high-performance battery applications."
            : productType === "inverter"
              ? "The Zynvert Inverter is built for the power demands of modern Indian homes and offices."
              : productType === "integrated_inverter"
                ? "The Zynvert Integrated System is the complete answer to every power cut scenario."
                : productType === "charger"
                  ? "High-speed charging solutions built specifically for demanding energy storage setups."
                  : "Zynvert Lithium is engineered for diverse, heavy-duty applications."}
        </p>
        <div className="app-grid">
          {(productType === "bms"
            ? [
                [
                  "🔧",
                  "DIY Battery Packs",
                  "Build your own high-performance lithium pack with professional-grade protection.",
                ],
                [
                  "☀️",
                  "Solar Storage",
                  "Pair with prismatic cells for a custom off-grid solar storage system.",
                ],
                [
                  "🚗",
                  "EV Retrofitting",
                  "Add smart BMS protection to electric vehicle and e-bike battery packs.",
                ],
                [
                  "🏭",
                  "Industrial Use",
                  "Protect lithium packs in UPS, telecom, and industrial backup applications.",
                ],
              ]
            : productType === "inverter" ||
                productType === "integrated_inverter"
              ? [
                  [
                    "🏠",
                    "Home Backup",
                    "Silent, instant power for lights, fans, ACs, and sensitive electronics.",
                  ],
                  [
                    "💻",
                    "Work From Home",
                    "Keep routers, computers, and modems running — no dropped calls or lost work.",
                  ],
                  [
                    "🏢",
                    "Office & Commercial",
                    "Protect billing systems, POS terminals, and CCTV from power cuts.",
                  ],
                  [
                    "☀️",
                    "Solar Ready",
                    "Pair with any solar charge controller for a full off-grid system.",
                  ],
                ]
              : productType === "charger"
                ? [
                    [
                      "🔋",
                      "Standalone Batteries",
                      "Safely charge your 12V, 24V or 48V independent lithium packs.",
                    ],
                    [
                      "🚗",
                      "EV & E-Rickshaws",
                      "Rapid charging to get your electric vehicles back on the road faster.",
                    ],
                    [
                      "☀️",
                      "Solar Backup",
                      "Perfect for quickly topping up solar-drained batteries via grid power.",
                    ],
                    [
                      "⚙️",
                      "Custom Projects",
                      "Reliable charging source for your DIY lithium storage projects.",
                    ],
                  ]
                : [
                    [
                      "🏠",
                      "Home Inverters",
                      "Perfect drop-in replacement for bulky tubular batteries.",
                    ],
                    [
                      "☀️",
                      "Solar Systems",
                      "98% charge efficiency — capture every drop of sunshine.",
                    ],
                    [
                      "🏢",
                      "Commercial Backup",
                      "Reliable power for shops, clinics, and offices.",
                    ],
                    [
                      "🚐",
                      "Off-Grid & RVs",
                      "Lightweight, vibration-resistant companion for mobile living.",
                    ],
                  ]
          ).map(([icon, title, desc]) => (
            <div className="app-card" key={title}>
              <div className="app-icon-wrapper">{icon}</div>
              <h4>{title}</h4>
              <p>{desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="how-it-works-section scroll-reveal slide-up delay-200">
        <h3>
          {productType === "bms"
            ? "Wiring your BMS is as easy as 1-2-3"
            : productType === "inverter"
              ? "Setting up your Zynvert Inverter in 3 steps"
              : productType === "integrated_inverter"
                ? "Up and running in 3 simple steps"
                : productType === "charger"
                  ? "Charging your battery safely in 3 steps"
                  : "Upgrading is as easy as 1-2-3"}
        </h3>
        <p className="section-subtitle">
          {productType === "bms"
            ? "Follow the sequence carefully for safe, correct installation."
            : productType === "charger"
              ? "Simple plug-and-play operation with no complicated settings."
              : "No complicated wiring or special technicians required."}
        </p>
        <div className="steps-container">
          {(productType === "bms"
            ? [
                [
                  "Connect B-",
                  "Attach the B- wire to the negative terminal of your cell stack first.",
                ],
                [
                  "Wire Balance Harness",
                  "Connect balance wires from B1 upward in sequence per the diagram.",
                ],
                [
                  "Charge to Activate",
                  "Apply a brief charge to wake the BMS firmware before load testing.",
                ],
              ]
            : productType === "integrated_inverter"
              ? [
                  [
                    "Unbox & Position",
                    "Place the unit in a ventilated spot and keep fan vents unobstructed.",
                  ],
                  [
                    "Plug into Wall",
                    "Connect the input cable to a standard 5A wall socket — that's it.",
                  ],
                  [
                    "Switch On & Enjoy",
                    "Power the unit on and enjoy years of silent, reliable backup.",
                  ],
                ]
              : productType === "inverter"
                ? [
                    [
                      "Connect Battery",
                      "Wire your Zynvert or compatible battery to the inverter terminals.",
                    ],
                    [
                      "Plug In Loads",
                      "Connect your appliances to the inverter's output sockets.",
                    ],
                    [
                      "Set Lithium Mode",
                      "Select 'Lithium' or 'LiFePO4' charging mode and power on.",
                    ],
                  ]
                : productType === "charger"
                  ? [
                      [
                        "Connect to Battery",
                        "Securely attach the positive (Red) and negative (Black) clamps to your battery.",
                      ],
                      [
                        "Plug to Wall Outlet",
                        "Connect the charger to a standard AC wall socket and turn the switch on.",
                      ],
                      [
                        "Auto-Charge & Complete",
                        "The LED indicators will show charging progress. It auto-cuts when 100% full.",
                      ],
                    ]
                  : [
                      [
                        "Disconnect Old Battery",
                        "Turn off your inverter and remove the old lead-acid battery.",
                      ],
                      [
                        "Connect Zynvert Lithium",
                        "Attach positive and negative terminals to your new Zynvert battery.",
                      ],
                      [
                        "Enjoy Peace of Mind",
                        "Turn your inverter on and enjoy years of maintenance-free power.",
                      ],
                    ]
          ).map(([title, desc], i) => (
            <React.Fragment key={title}>
              <div className="step-box">
                <div className="step-number">{i + 1}</div>
                <h4>{title}</h4>
                <p>{desc}</p>
              </div>
              {i < 2 && <div className="step-connector" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="faq-section scroll-reveal slide-up delay-100">
        <h3>Frequently Asked Questions</h3>
        <div className="faq-list">
          {(productType === "bms"
            ? [
                [
                  "Is this compatible with my LiFePO4 cells?",
                  "Yes. This BMS is specifically designed for 4S (4-cell series) LiFePO4 configurations with a 12.8V nominal voltage. It will not work correctly with Li-ion, LiPo, or lead-acid chemistries.",
                ],
                [
                  "What happens if I wire it incorrectly?",
                  "Incorrect wiring can permanently damage the BMS. Always follow the included wiring diagram precisely — connect B- first, then balance wires from B1 upward.",
                ],
                [
                  "Can I monitor it without a smartphone?",
                  "The BMS operates fully without the app. Bluetooth monitoring is an optional feature for real-time visibility into cell voltages, temperature, and fault codes.",
                ],
                [
                  "Does conformal coating mean it's waterproof?",
                  "No — it is humidity and dust resistant. Do not submerge the board or expose it to direct water spray.",
                ],
              ]
            : productType === "inverter" ||
                productType === "integrated_inverter"
              ? [
                  [
                    "Will this work with my existing battery?",
                    "Yes. This inverter supports LiFePO4, Li-ion, Tubular Lead-Acid, and SMF batteries. For lithium batteries, ensure you select the 'Lithium' charging mode for optimal performance.",
                  ],
                  [
                    "Will my computer restart during a power cut?",
                    "No. The <10ms transfer time is faster than any computer's UPS threshold — your devices will not even register the switch to battery mode.",
                  ],
                  [
                    "How loud is the fan?",
                    "The thermostatically controlled fan only activates under heavy load or high temperature. During normal use, it runs silently or not at all.",
                  ],
                  [
                    "Can I add solar panels to this inverter?",
                    productType === "integrated_inverter"
                      ? "Yes — the integrated system supports connection to external solar charge controllers."
                      : "Yes. The inverter is compatible with most external solar charge controllers connected at the battery terminals.",
                  ],
                ]
              : productType === "charger"
                ? [
                    [
                      "Will this overcharge my battery if left plugged in?",
                      "No. Zynventics chargers feature a smart auto cut-off logic that completely stops supplying current once the battery reaches 100%.",
                    ],
                    [
                      "Can I use this to charge my old Lead-Acid battery?",
                      "This charger is specifically optimized with a charging voltage profile for Lithium/LiFePO4. Using it on lead-acid is not recommended.",
                    ],
                    [
                      "Is it normal for the charger to get warm?",
                      "Yes, it is completely normal for high-current chargers to become warm during operation. It features an active fan that will regulate the temperature safely.",
                    ],
                    [
                      "What do the LED lights mean?",
                      "Usually, RED indicates the battery is actively charging, and GREEN indicates the battery is 100% full and the charger has cut off power.",
                    ],
                  ]
                : [
                    [
                      "Is this compatible with my existing inverter?",
                      "Yes, as long as your inverter supports lithium or LiFePO4 charging profiles. Most modern inverters have a lithium mode. Contact our team to verify compatibility.",
                    ],
                    [
                      "Is it safe to keep this battery indoors?",
                      "Absolutely. LiFePO4 chemistry and our built-in Smart BMS make this 100% safe — no toxic gas emissions, no acid leaks, no fire risk under normal use.",
                    ],
                    [
                      "How fast does it charge?",
                      "Zynvert Lithium charges up to 3× faster than lead-acid. A fully depleted battery can reach 100% in 2–4 hours with a compatible charger.",
                    ],
                    [
                      "Can I discharge it to 0%?",
                      "Yes. Unlike lead-acid which degrades rapidly below 50%, our batteries support 100% Depth of Discharge safely — you use every unit of energy you paid for.",
                    ],
                    [
                      "Does it require maintenance?",
                      "Zero. No acid level checks, no terminal cleaning, no distilled water top-ups. Ever.",
                    ],
                    [
                      "What is the estimated lifespan?",
                      "2000+ charge cycles — typically 5–7 years of daily use before any measurable capacity degradation.",
                    ],
                    [
                      "Do you offer a warranty?",
                      `Yes — ${parsedSpecs?.warranty?.duration_years || 1} Year ${parsedSpecs?.warranty?.type || "Replacement Warranty"}. Manufacturing defects are covered directly by Zynvert Technologies.`,
                    ],
                  ]
          ).map(([q, a]) => (
            <details className="faq-item" key={q}>
              <summary>{q}</summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </div>

      <div className="trust-strip-section scroll-reveal slide-up">
        {[
          ["🚚", "Free Pan-India Delivery", "Secure & fast shipping"],
          ["🇮🇳", "Make in India", "Premium local manufacturing"],
          ["🛡️", "100% Safe & Tested", "BIS Certified components"],
          ["📞", "Expert Support", "Direct help from our engineers"],
        ].map(([icon, title, sub]) => (
          <div className="trust-strip-item" key={title}>
            <span className="trust-icon">{icon}</span>
            <div>
              <strong>{title}</strong>
              <span>{sub}</span>
            </div>
          </div>
        ))}
      </div>

      {relatedProducts.length > 0 && (
        <div className="related-products-section scroll-reveal slide-up delay-100">
          <h2 className="related-title">Related Products</h2>
          <div className="related-products-grid">
            {relatedProducts.map((item, index) => (
              <div
                key={item._id}
                className={`scroll-reveal slide-up delay-${((index % 4) + 1) * 100}`}
              >
                <ProductCard
                  product={item}
                  productId={item.product_id}
                  onNavigate={onNavigate}
                  onAddToCart={onAddToCart}
                  cartItems={cartItems}
                  onQuantityChange={onQuantityChange}
                  onRemoveItem={onRemoveItem}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        ref={reviewsRef}
        className="review-scroll-target"
        style={{ scrollMarginTop: "100px" }}
      >
        <ReviewSection
          productId={productId}
          productName={name}
          productImage={images?.length > 0 ? images[0] : FALLBACK_IMAGE}
        />
      </div>

      <div className="mobile-buy-bar">
        <div className="mobile-buy-inner">
          <div className="mobile-price-box">
            {old_price > price_display && (
              <span className="mobile-old-price">₹{old_price}</span>
            )}
            <span className="mobile-price">₹{price_display}</span>
          </div>

          {isOutOfStock ? (
            <button
              className="mobile-buy-btn notify"
              disabled={
                notifyStatus === "loading" || notifyStatus === "success"
              }
              onClick={handleNotifyMeClick}
              style={{
                backgroundColor:
                  notifyStatus === "success" ? "#dcfce3" : "#1f2937",
                color: notifyStatus === "success" ? "#166534" : "white",
                border: "none",
              }}
            >
              {notifyStatus === "loading"
                ? "Wait..."
                : notifyStatus === "success"
                  ? "✅ Added"
                  : "🔔 Notify Me"}
            </button>
          ) : (
            <button
              className="mobile-buy-btn"
              onClick={async () => {
                try {
                  setIsBuyingNow(true);
                  if (quantity === 0) await onAddToCart(product);
                  setTimeout(() => onNavigate("cart"), 600);
                } catch (err) {
                  setIsBuyingNow(false);
                }
              }}
            >
              Buy Now
            </button>
          )}
        </div>
      </div>

      {showCartSuccess && (
        <div className="cart-success-toast">
          <span>✔ Item successfully added to cart</span>
        </div>
      )}
      {isBuyingNow && (
        <div className="buy-loader-overlay">
          <div className="buy-loader-box">
            <div className="spinner"></div>
            <p>Redirecting to checkout...</p>
          </div>
        </div>
      )}

      {isModalOpen &&
        createPortal(
          <div
            className="flipkart-modal-overlay"
            onClick={() => setIsModalOpen(false)}
          >
            <button
              className="modal-close-btn"
              onClick={() => setIsModalOpen(false)}
            >
              ✕
            </button>

            <div
              className="modal-content-center"
              onClick={(e) => e.stopPropagation()}
            >
              {images.length > 1 && (
                <button className="modal-nav-btn prev" onClick={prevImage}>
                  ❮
                </button>
              )}

              <img
                src={images[activeImageIndex] || FALLBACK_IMAGE}
                alt="Enlarged product"
                className="modal-main-img"
              />

              {images.length > 1 && (
                <button className="modal-nav-btn next" onClick={nextImage}>
                  ❯
                </button>
              )}
            </div>

            <div className="modal-dots-container">
              {images.map((_, i) => (
                <div
                  key={i}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImageIndex(i);
                  }}
                  className={`modal-dot ${activeImageIndex === i ? "active" : ""}`}
                  style={{ cursor: "pointer" }}
                />
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ProductDetailPage;
